import { useMutation } from '@tanstack/vue-query'
import { useToast } from 'vue-toastification'

import { Filters } from '@/modules/reports/pages/HotelReportsPage.vue'
import { getHotelReport } from '@/modules/reports/services/hotelReportApi'

interface ExportParams {
	companyId: number
	filters: ExportFilters
	format: 'pdf' | 'csv'
}

interface ExportFilters {
	dates?: string[] | null
	creationDates?: string[] | null
	status?: string
	groupBy?: string
	travelerId?: number
	hotelId?: number
	createdAt?: string[] | null
	orderBy?: string
	orderDirection?: 'asc' | 'desc'
}

// La descarga usa responseType 'blob', así que un error (p.ej. 422 "No hay
// datos para exportar con los filtros seleccionados") llega con el cuerpo como
// Blob. Lo parseamos para sacar el message del backend y mostrarlo en toast.
const extractBlobMessage = async (
	data: unknown
): Promise<string | undefined> => {
	if (data instanceof Blob) {
		try {
			return JSON.parse(await data.text())?.message
		} catch {
			return undefined
		}
	}
	return (data as { message?: string })?.message
}

const exportCompanyReportHotels = async (params: ExportParams) => {
	const toast = useToast()

	let response
	try {
		response = await getHotelReport<Blob>(params.companyId, 'export', {
			params: {
				dates: params.filters?.dates,
				createdDate: params.filters?.creationDates,
				status: params.filters?.status,
				groupBy: params.filters?.groupBy,
				traveler: params.filters?.travelerId,
				hotel: params.filters?.hotelId,
				createdAt: params.filters?.createdAt,
				sortBy: params.filters?.orderBy,
				sortDirection: params.filters?.orderDirection,
				format: params.format,
			},
			responseType: 'blob',
		})
	} catch (err) {
		// 422 (sin datos, etc.): mostramos el mensaje del server y salimos sin
		// romper —resolvemos para que el botón "Exportando…" se resetee.
		const message = await extractBlobMessage(
			(err as { response?: { data?: unknown } })?.response?.data
		)
		toast.error(message || 'No se pudo exportar el reporte. Intenta de nuevo.')
		return false
	}

	const blob = new Blob([response.data], {
		type:
			params.format === 'pdf' ? 'application/pdf' : 'text/csv;charset=utf-8;',
	})

	const url = window.URL.createObjectURL(blob)
	const link = document.createElement('a')
	link.href = url

	const date = new Date().toISOString().split('T')[0]
	link.download = `reporte-hoteles-${date}.${params.format}`

	document.body.appendChild(link)
	link.click()
	document.body.removeChild(link)
	window.URL.revokeObjectURL(url)

	return true
}

export default function useExportCompanyReportHotels() {
	const {
		mutateAsync: exportReport,
		isLoading: isExporting,
		error,
	} = useMutation({
		mutationFn: exportCompanyReportHotels,
		onError: (err) => {
			console.error('Error al exportar reporte:', err)
		},
	})

	return {
		exportReport,
		isExporting,
		error,
	}
}
